import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useVehicles } from '../../../hooks/useVehicles';
import VehicleCard from '../../inventory/components/VehicleCard';
import NearbyAreasSection from '../components/NearbyAreasSection';
import ReadyToShopCta from '../components/ReadyToShopCta';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';

export default function GearhartPage() {
  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://rockhoundauto.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Gearhart', item: 'https://rockhoundauto.com/service-areas/gearhart' },
      ],
    };
    injectJsonLd('schema-gearhart-breadcrumb', breadcrumbSchema);
    return () => { removeJsonLd('schema-gearhart-breadcrumb'); };
  }, []);

  useSEOHead({
    title: 'Used Cars Gearhart Oregon — Quiet Coast, Zero Dealership Pressure | Rockhound Auto',
    description: 'Buy quality used cars in Gearhart, Oregon without dealership hassle. Straight pricing, fast process, free delivery to Gearhart. Serving Seaside, Cannon Beach & Oregon Coast.',
    keywords: 'used cars gearhart oregon, cars for sale gearhart, gearhart auto sales, oregon coast vehicles, seaside cars, cannon beach auto',
    canonicalUrl: 'https://rockhoundauto.com/service-areas/gearhart',
    ogTitle: 'Used Cars in Gearhart Oregon | Rockhound Auto',
    ogDescription: 'Skip the dealership circus. Buy quality used cars in Gearhart with straight pricing and fast delivery.',
    ogUrl: 'https://rockhoundauto.com/service-areas/gearhart'
  });

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const nearbyAreas = [
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Warrenton', slug: 'warrenton' },
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Cannon Beach', slug: 'cannon-beach' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/6b9e05fc97c574658c95e683b1dbb79f.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            {/* CONTRAST FIX: text-teal-400 → text-teal-300 for WCAG AA on dark bg */}
            <div className="flex items-center gap-2 text-teal-300 mb-4">
              <i className="ri-map-pin-line text-xl"></i>
              <span className="font-semibold">Serving Gearhart, Oregon</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Used Cars in Gearhart, Oregon
            </h1>
            {/* CONTRAST FIX: text-slate-300 → text-slate-200 */}
            <p className="text-2xl text-slate-200 mb-8">
              Buy a Car. Don&apos;t Hate the Process.
            </p>
            {/* CONTRAST FIX: text-slate-400 → text-slate-300 */}
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Straight answers. Fair numbers. Free delivery to Gearhart. No dealership games, no pressure, no wasted weekends. Just quality used cars and a process that respects your time.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/inventory" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                View Available Inventory
              </Link>
              <Link to="/broker-services" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap border border-white/20">
                Auto Broker Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="bg-white py-8 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">15 min</div>
              <div className="text-slate-600 text-sm">Drive from Gearhart</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Free</div>
              <div className="text-slate-600 text-sm">Delivery to Gearhart</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">No Pressure</div>
              <div className="text-slate-600 text-sm">Sales Tactics</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">Straight</div>
              <div className="text-slate-600 text-sm">Pricing Always</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Gearhart Residents Choose Rockhound */}
      <section className="py-16 bg-slate-50" aria-label="Why Gearhart residents choose Rockhound Auto">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Gearhart Residents Choose Rockhound Auto
            </h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-6">
              <p>
                Living in Gearhart means you value quality, peace, and authenticity. You chose this quiet coastal community because it&apos;s the opposite of chaos and noise. So why would you want to buy a car the traditional dealership way?
              </p>
              <p><strong>You shouldn&apos;t. And you don&apos;t have to.</strong></p>
              <p>
                Rockhound Auto brings a completely different car-buying experience to Gearhart residents. We&apos;re located just 15 minutes away in Warrenton, but we deliver for free to your driveway. No high-pressure sales floors. No finance office games. No wasting your Saturday at a dealership.
              </p>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">What Makes Us Different for Gearhart Buyers</h3>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                  We Respect Your Time
                </h4>
                <p className="text-slate-700 mb-0">
                  Browse our inventory online. Ask questions via text or call. Schedule a quick test drive in Gearhart or at our lot. Make a decision without pressure. We can complete the entire process in under an hour, or take days if you prefer.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                  Straight Pricing, No Games
                </h4>
                <p className="text-slate-700 mb-0">
                  The price you see online is the real price. Not a "starting point for negotiation." We don&apos;t play the four-square game, we don&apos;t have a "manager" who needs to approve anything, and we don&apos;t add mystery fees at the end.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 my-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                  Free Delivery to Your Gearhart Home
                </h4>
                <p className="text-slate-700 mb-0">
                  Found the right vehicle? We&apos;ll bring it to you. Free delivery anywhere in Gearhart. Test drive it at your house, on your streets, in your driveway. If you love it, we&apos;ll complete the paperwork right there.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What Gearhart Buyers Are Looking For</h3>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                {[
                  { icon: 'ri-shield-check-line', title: 'Coastal-Ready Vehicles', desc: 'Salt air, moisture, and coastal weather demand vehicles with rust protection, good seals, and reliable mechanics. We inspect for coastal durability.' },
                  { icon: 'ri-car-line', title: 'AWD & 4WD Options', desc: 'Winter storms, wet roads, and occasional snow mean AWD and 4WD are popular. We stock Subarus, trucks, and SUVs built for Oregon weather.' },
                  { icon: 'ri-gas-station-line', title: 'Fuel Efficiency', desc: 'With Seaside and Astoria being the closest towns for shopping, fuel economy matters. We highlight MPG ratings and real-world efficiency.' },
                  { icon: 'ri-tools-line', title: 'Low Maintenance Costs', desc: 'Reliable brands like Toyota, Honda, Subaru, and Mazda are favorites. We focus on vehicles with proven track records and affordable parts.' },
                ].map((item) => (
                  <div key={item.title} className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <i className={`${item.icon} text-xl text-teal-600`}></i>
                      {item.title}
                    </h4>
                    <p className="text-slate-700 text-sm mb-0">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-red-900 mb-3">The Traditional Dealership Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Drive 30+ minutes to Longview, Portland, or beyond','Spend 3-4 hours at the dealership (minimum)','Deal with high-pressure sales tactics and "manager approvals"','Negotiate pricing, then get hit with surprise fees in finance','Leave exhausted, wondering if you got a fair deal'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-1"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 my-6">
                <h4 className="font-bold text-teal-900 mb-3">The Rockhound Experience:</h4>
                <ul className="space-y-2 text-slate-700">
                  {['Browse inventory from your couch in Gearhart','Text or call with questions—get straight answers','Free delivery to your Gearhart driveway for test drives','Transparent pricing with no hidden fees or surprises','Complete the deal in under an hour, or take your time—your choice'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-1 font-bold"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-8 my-8">
                <h4 className="text-2xl font-bold mb-4">Get Pre-Qualified in Minutes</h4>
                <p className="text-slate-300 mb-6">See your financing options before you even visit. No impact on your credit score, no obligation.</p>
                <Link to="/apply-for-financing" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Apply for Financing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-white" aria-label="Available vehicles near Gearhart">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Available Vehicles Near Gearhart</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Browse our current inventory. All vehicles are inspected, detailed, and ready for delivery to Gearhart.
            </p>
          </div>

          {/* TOUCH TARGET FIX: py-2 → py-3, added min-h-[44px] */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" role="group" aria-label="Filter vehicles by type">
            {[
              { label: 'All Vehicles', value: 'all' },
              { label: 'SUVs', value: 'SUV' },
              { label: 'Trucks', value: 'Truck' },
              { label: 'Sedans', value: 'Sedan' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setSelectedType(value)}
                aria-pressed={selectedType === value}
                className={`px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap min-h-[44px] cursor-pointer ${
                  selectedType === value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap">
                  View All Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">No vehicles currently match your filter. Check back soon or view all inventory.</p>
              <Link to="/inventory" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-16 bg-slate-50" aria-label="Gearhart service area map">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center text-slate-900">
              Free Delivery to Gearhart &amp; Surrounding Areas
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11174.5!2d-123.9185!3d46.0271!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54937c0000000001%3A0xabcdef0000000001!2sGearhart%2C%20OR%2097138!5e0!3m2!1sen!2sus!4v1700000000003!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Gearhart Oregon Service Area Map"
                ></iframe>
              </div>
              <div className="p-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { icon: 'ri-map-pin-line', title: 'Our Location', desc: 'Warrenton, OR\n15 min from Gearhart' },
                    { icon: 'ri-truck-line', title: 'Free Delivery', desc: 'To your Gearhart\ndriveway' },
                    { icon: 'ri-time-line', title: 'Fast Process', desc: 'Complete in under\nan hour' },
                  ].map((item) => (
                    <div key={item.title} className="text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className={`${item.icon} text-3xl text-teal-600`}></i>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm whitespace-pre-line">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Gearhart" nearbyAreas={nearbyAreas} />
      <ReadyToShopCta cityName="Gearhart" />
    </>
  );
}
